import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { getAppBaseUrl, getBrandingAssetUrl } from '@/utils/runtimeApi';
import {
  Home,
  Package,
  Layers,
  FileText,
  Calculator,
  Settings,
  Users,
  Bell,
  BarChart3,
  LogOut,
  Menu,
  X,
  Bike,
  Award,
  ClipboardList,
  TrendingUp,
  Shield,
  RefreshCw,
  ShoppingCart,
  CheckSquare,
  ShoppingBag,
  DollarSign,
  Target,
  Calendar
} from 'lucide-react';
import { api } from '@/contexts/AuthContext';

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const eventSourceRef = useRef(null);
  const [expandedCategories, setExpandedCategories] = useState(['dashboard', 'pricing']); // Varsayılan açık kategoriler

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  useEffect(() => {
    fetchUnreadNotifications();
    connectSSE();
    
    return () => {
      // Cleanup SSE bağlantısı
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [user]);

  useEffect(() => {
    const handleUnreadUpdate = (event) => {
      setUnreadCount(event.detail?.unreadCount || 0);
    };

    window.addEventListener('notifications-unread-updated', handleUnreadUpdate);
    return () => window.removeEventListener('notifications-unread-updated', handleUnreadUpdate);
  }, []);

  const connectSSE = () => {
    if (!user?.id) return;

    const backendUrl = getAppBaseUrl();
    const sseUrl = `${backendUrl}/api/notifications/stream/${user.id}`;
    
    try {
      console.log('SSE: Bağlanıyor...');
      const eventSource = new EventSource(sseUrl, { withCredentials: true });
      eventSourceRef.current = eventSource;
      
      eventSource.onopen = () => {
        console.log('✅ SSE bağlantısı başarılı');
      };
      
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Heartbeat'i görmezden gel
          if (data.type === 'heartbeat') {
            return;
          }
          
          // İlk bağlantı mesajı
          if (data.type === 'init') {
            setUnreadCount(data.unread_count || 0);
            return;
          }

          if (data.type === 'badge_update') {
            setUnreadCount(data.unread_count || 0);
            return;
          }
          
          // Yeni bildirim
          setUnreadCount(prev => prev + 1);
          
          // Toast göster
          const icon = data.icon || '🔔';
          toast.info(`${icon} ${data.title}`, {
            description: data.message,
            duration: 5000
          });
        } catch (error) {
          console.error('SSE mesaj parse hatası:', error);
        }
      };
      
      eventSource.onerror = (error) => {
        console.error('SSE bağlantı hatası:', error);
        eventSource.close();
        
        // 30 saniye sonra yeniden bağlan
        setTimeout(() => {
          console.log('SSE: Yeniden bağlanılıyor...');
          connectSSE();
        }, 30000);
      };
    } catch (error) {
      console.error('SSE başlatma hatası:', error);
    }
  };

  const fetchUnreadNotifications = async () => {
    if (!user?.id) return;
    
    try {
      const response = await api.get(`/notifications?user_id=${user.id}&unread_only=true`);
      const unread = Array.isArray(response.data)
        ? response.data.length
        : (response.data.unread_count || 0);
      setUnreadCount(unread);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const hasPermissionFallback = (pageId) => {
    const permissions = user?.detailed_permissions || {};

    const pagePermissionChecks = {
      'role-templates': () => permissions.user_management?.can_manage_role_templates === true,
      'users': () => permissions.user_management?.can_view_users === true || permissions.user_management?.can_manage_roles === true,
      'branches': () => permissions.branches?.can_view_branches === true,
      'settings': () => permissions.settings?.can_view_settings === true,
      'factory-prices': () => permissions.factory_prices?.can_view_factory_prices === true,
      'decision-support': () => permissions.decision_support?.can_view_analytics === true || permissions.decision_support?.can_view_forecasts === true,
      'manager-reports': () => permissions.reports?.can_view_performance === true || permissions.reports?.can_view_sales_reports === true || permissions.tasks?.can_view_all_tasks === true,
      'crm': () => permissions.crm?.can_view_customers === true,
      'appointments': () => permissions.crm?.can_view_customers === true,
      'insurance': () => permissions.insurance?.can_view_requests === true,
      'tasks': () => permissions.tasks?.can_view_tasks === true,
      'refunds': () => permissions.refunds?.can_view_refunds === true,
      'brands': () => permissions.brands?.can_view_brands === true,
      'models': () => permissions.models?.can_view_models === true,
      'bonus': () => permissions.bonus_system?.can_view_bonus === true,
      'purchases': () => permissions.procurement?.can_view_purchases === true,
      'performance': () => permissions.reports?.can_view_performance === true,
    };

    return pagePermissionChecks[pageId]?.() || false;
  };

  const canSeeNavItem = (item) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;

    const pageId = item.path.replace('/', '');
    const accessiblePages = user.accessible_pages || [];

    if (accessiblePages.includes(pageId)) {
      return true;
    }

    if (item.roles?.includes(user.role)) {
      return true;
    }

    return hasPermissionFallback(pageId);
  };

  const navCategories = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: Home,
      color: 'text-blue-600',
      items: [
        { path: '/dashboard', icon: Home, label: 'Gösterge Paneli', roles: ['super_admin', 'manager', 'sales_manager', 'sales_support', 'sales_consultant'] },
        { path: '/manager-reports', icon: ClipboardList, label: 'Müdür Raporları', roles: ['super_admin', 'manager', 'sales_manager', 'service_manager', 'sales_support'] },
      ]
    },
    {
      id: 'pricing',
      label: 'Fiyatlandırma',
      icon: Calculator,
      color: 'text-green-600',
      items: [
        { path: '/calculator', icon: Calculator, label: 'Hesaplama', roles: ['super_admin', 'manager', 'sales_manager', 'sales_support', 'sales_consultant'] },
        { path: '/price-lists', icon: FileText, label: 'Fiyat Listeleri', roles: ['super_admin', 'manager', 'sales_manager', 'sales_support', 'sales_consultant', 'digital_marketing'] },
      ]
    },
    {
      id: 'sales',
      label: 'Satış & Stok',
      icon: ShoppingCart,
      color: 'text-purple-600',
      items: [
        { path: '/sales-entry', icon: ClipboardList, label: 'Satış Girişi', roles: ['super_admin', 'manager', 'sales_manager', 'sales_support'] },
        { path: '/performance', icon: TrendingUp, label: 'Performans', roles: ['super_admin', 'manager', 'sales_manager', 'sales_support', 'sales_consultant'] },
        { path: '/bonus', icon: Award, label: 'Hedef & Prim', roles: ['super_admin', 'manager', 'sales_manager', 'sales_support', 'sales_consultant'] },
        { path: '/preorders', icon: ShoppingCart, label: 'Ön Sipariş', roles: ['super_admin', 'manager', 'sales_manager', 'sales_consultant'] },
      ]
    },
    {
      id: 'customer',
      label: 'Müşteri İlişkileri',
      icon: Users,
      color: 'text-orange-600',
      items: [
        { path: '/crm', icon: Users, label: 'CRM', roles: ['super_admin', 'manager', 'customer_relations', 'customer_relations_manager'] },
        { path: '/appointments', icon: Calendar, label: 'Randevular', roles: ['super_admin', 'manager', 'customer_relations', 'customer_relations_manager'] },
      ]
    },
    {
      id: 'insurance',
      label: 'Sigorta',
      icon: Shield,
      color: 'text-cyan-600',
      items: [
        { path: '/insurance', icon: Shield, label: 'Sigorta', roles: ['super_admin', 'manager', 'sales_manager', 'sales_support', 'sales_consultant', 'insurance_consultant', 'customer_relations', 'customer_relations_manager'] },
      ]
    },
    {
      id: 'tasks',
      label: 'Görevler',
      icon: CheckSquare,
      color: 'text-teal-600',
      items: [
        { path: '/tasks', icon: CheckSquare, label: 'Görevler', roles: ['super_admin', 'manager', 'sales_manager', 'sales_consultant', 'customer_relations', 'customer_relations_manager'] },
      ]
    },
    {
      id: 'finance',
      label: 'Finans - Satın Alma',
      icon: DollarSign,
      color: 'text-emerald-600',
      items: [
        { path: '/purchases', icon: ShoppingBag, label: 'Genel Satın Alma (Yedek Parça, Kırtasiye)', roles: ['super_admin', 'manager', 'sales_manager', 'sales_consultant'] },
        { path: '/refunds', icon: RefreshCw, label: 'İade Yönetimi', roles: ['super_admin', 'manager', 'sales_manager', 'sales_support', 'sales_consultant', 'customer_relations', 'customer_relations_manager'] },
      ]
    },
    {
      id: 'marketing',
      label: 'Pazarlama',
      icon: TrendingUp,
      color: 'text-pink-600',
      items: [
        { path: '/campaigns', icon: TrendingUp, label: 'Kampanya', roles: ['super_admin', 'digital_marketing'] },
      ]
    },
    {
      id: 'products',
      label: 'Ürün Yönetimi',
      icon: Package,
      color: 'text-indigo-600',
      items: [
        { path: '/brands', icon: Package, label: 'Markalar', roles: ['super_admin', 'manager', 'sales_manager'] },
        { path: '/models', icon: Layers, label: 'Modeller', roles: ['super_admin', 'manager', 'sales_manager'] },
        { path: '/factory-prices', icon: Package, label: 'Factory Price Set', roles: ['super_admin', 'manager', 'sales_manager'] },
      ]
    },
    {
      id: 'settings',
      label: 'Yönetim',
      icon: Settings,
      color: 'text-gray-600',
      items: [
        { path: '/decision-support', icon: BarChart3, label: 'Karar Destek', roles: ['super_admin', 'manager'] },
        { path: '/performance-analysis', icon: Target, label: 'Performans Analizi', roles: ['super_admin', 'admin', 'manager'] },
        { path: '/ml-forecasting', icon: TrendingUp, label: 'ML Tahminleme', roles: ['super_admin', 'admin', 'manager'] },
        { path: '/procurement-decision', icon: Calculator, label: 'Motor Satın Alma Karar Katmanı', roles: ['super_admin', 'admin'] },
        { path: '/demo-access', icon: Users, label: 'Demo Erişim Merkezi', roles: ['super_admin'] },
        { path: '/branches', icon: Home, label: 'Şubeler', roles: ['super_admin'] },
        { path: '/users', icon: Users, label: 'Kullanıcılar', roles: ['super_admin'] },
        { path: '/role-templates', icon: Shield, label: 'Rol Şablonları', roles: ['super_admin'] },
        { path: '/settings', icon: Settings, label: 'Ayarlar', roles: ['super_admin'] },
      ]
    },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-border transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-border">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 flex-shrink-0 bg-white rounded overflow-hidden">
                <img src={getBrandingAssetUrl('logo')} alt="Uçar Motor" className="w-full h-full object-contain" />
              </div>
              <div>
                <h1 className="text-lg font-bold">Uçar Motor</h1>
                <p className="text-xs text-muted-foreground">Fiyat Yönetimi</p>
              </div>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-2 overflow-y-auto" data-testid="dashboard-nav">
            {navCategories.map((category) => {
              const visibleItems = category.items.filter(canSeeNavItem);
              
              // Hiç görünür menü yoksa kategoriyi gösterme
              if (visibleItems.length === 0) return null;
              
              const CategoryIcon = category.icon;
              const isExpanded = expandedCategories.includes(category.id);
              const hasActiveItem = visibleItems.some(item => location.pathname === item.path);
              
              return (
                <div key={category.id} className="space-y-1">
                  {/* Kategori Başlığı */}
                  <button
                    onClick={() => toggleCategory(category.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                      hasActiveItem ? 'bg-primary/10 text-primary' : 'hover:bg-secondary text-muted-foreground'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <CategoryIcon className={`w-4 h-4 ${category.color}`} />
                      <span className="text-sm font-semibold">{category.label}</span>
                    </div>
                    <span className={`text-xs transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                      ▼
                    </span>
                  </button>
                  
                  {/* Alt Menüler (Accordion) */}
                  {isExpanded && (
                    <div className="ml-3 space-y-0.5 border-l-2 border-secondary pl-2">
                      {visibleItems.map((item) => {
                        const ItemIcon = item.icon;
                        const isActive = location.pathname === item.path;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                              isActive
                                ? 'bg-primary text-primary-foreground font-medium'
                                : 'text-foreground hover:bg-secondary'
                            }`}
                            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                            onClick={() => setSidebarOpen(false)}
                          >
                            <ItemIcon className="w-4 h-4" />
                            <span>{item.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="p-4 border-t border-border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                  <span className="text-sm font-semibold">{user?.full_name?.charAt(0)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.full_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Çıkış Yap
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-40 glass-nav">
          <div className="flex items-center justify-between px-4 lg:px-8 h-16">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-secondary"
              data-testid="mobile-menu-button"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            <div className="flex-1 lg:flex-none"></div>

            <div className="flex items-center space-x-4">
              <Link to="/notifications" className="relative">
                <Button variant="ghost" size="icon" data-testid="notifications-button">
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <Badge
                      className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center p-0 text-xs"
                      data-testid="notification-badge"
                    >
                      {unreadCount}
                    </Badge>
                  )}
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">{children}</main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
}
